import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import moment from 'moment';
import styled from 'styled-components';
import BasicButton from 'components/Buttons/BasicButton';
import { adminManageUserJoinPageApi } from 'api/Admin/adminManageUserJoinPageApi';
import { userApi } from 'api/DCK/userApi';
import { GetUserListApiListType } from 'models/DCKAdmin/AdminManageUserJoinPageTypes';
import { AccountLevel, AccountStatus } from 'utils/statusCode';
import { changeMobileForm } from 'utils/changeMobileForm';
import { useLoadingStore } from 'store/useLoadingStore';
import { useLoginStore } from 'store/useLoginDataStore';
import { PageTitle } from 'styles/Global/commonStyles';
import { DiagnosisInnerLayout, DiagnosisLayout, HospitalPageLabel } from 'styles/Global/hospitalStyles';
import { BackgroundColor, BorderColor, TextColor, WarningColor } from 'styles/Global/colorSemantic';
import { Radius } from 'styles/Global/objectStyles';
import { Body2Bold, Body3Medium, Body3Regular, Body5Regular } from 'styles/Global/typography';

const PAGE_LIMIT = 11;

const SEARCH_FILTER_OPTIONS = [
    { label: '병원명', value: 1 },
    { label: '이메일', value: 2 },
    { label: '연락처', value: 3 },
];

const LEVEL_OPTIONS = [
    { label: '전체 권한', value: 'all' },
    { label: '일반 사용자', value: String(AccountLevel.default) },
    { label: '병원 관리자', value: String(AccountLevel.hospitalManager) },
    { label: '성남 관리자', value: String(AccountLevel.sungnam) },
    { label: '외부 상담사', value: String(AccountLevel.externalCounselor) },
    { label: '최고 관리자', value: String(AccountLevel.admin) },
    { label: '기타', value: String(AccountLevel.other) },
];

const CHANGEABLE_LEVEL_OPTIONS = LEVEL_OPTIONS.filter((option) => option.value !== 'all');

const getErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
        return [error.response?.status, error.response?.data?.error?.message || error.response?.data?.message || error.message]
            .filter(Boolean)
            .join(' ');
    }

    return error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.';
};

const getLevelLabel = (level: number) => {
    return CHANGEABLE_LEVEL_OPTIONS.find((option) => Number(option.value) === level)?.label || `알 수 없음(${level})`;
};

const getStatusLabel = (status: number) => {
    switch (status) {
        case AccountStatus.pending:
            return '승인 대기';
        case AccountStatus.active:
            return '승인 완료';
        case AccountStatus.reject:
            return '반려';
        default:
            return `알 수 없음(${status})`;
    }
};

const parseLevelFilter = (value: string) => {
    return value === 'all' ? null : Number(value);
};

const AdminUserRolePage = () => {
    const { loginData } = useLoginStore();
    const { setLoading } = useLoadingStore();

    const [users, setUsers] = useState<GetUserListApiListType[]>([]);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(0);
    const [totalCount, setTotalCount] = useState(0);
    const [searchFilter, setSearchFilter] = useState(SEARCH_FILTER_OPTIONS[0].value);
    const [searchValue, setSearchValue] = useState('');
    const [submittedSearch, setSubmittedSearch] = useState('');
    const [submittedSearchFilter, setSubmittedSearchFilter] = useState(SEARCH_FILTER_OPTIONS[0].value);
    const [levelFilter, setLevelFilter] = useState<string>('all');
    const [roleSelections, setRoleSelections] = useState<Record<string, number>>({});
    const [message, setMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [updatingUserId, setUpdatingUserId] = useState('');

    const selectedLevel = useMemo(() => parseLevelFilter(levelFilter), [levelFilter]);
    const hasPrevPage = page > 1;
    const hasNextPage = totalPages > 0 && page < totalPages;

    const fetchUserList = useCallback(async () => {
        if (!loginData.token) return;

        setLoading(true);
        setErrorMessage('');

        try {
            const keyword = submittedSearch.trim();
            const response = await adminManageUserJoinPageApi.getUserList(
                page,
                keyword ? submittedSearchFilter : null,
                keyword || null,
                loginData.token,
                selectedLevel
            );

            const userList = response.data?.list || [];
            setUsers(userList);
            setTotalPages(response.data?.pagination.totalPages || 0);
            setTotalCount(response.data?.pagination.totalCount || 0);
            setRoleSelections(Object.fromEntries(userList.map((user) => [user._id, user.level])));
        } catch (error) {
            setUsers([]);
            setTotalPages(0);
            setTotalCount(0);
            setErrorMessage(`관리자 목록을 불러오지 못했습니다. ${getErrorMessage(error)}`);
        } finally {
            setLoading(false);
        }
    }, [loginData.token, page, selectedLevel, setLoading, submittedSearch, submittedSearchFilter]);

    useEffect(() => {
        fetchUserList();
    }, [fetchUserList]);

    const handleSearch = (e: FormEvent) => {
        e.preventDefault();
        setPage(1);
        setSubmittedSearchFilter(searchFilter);
        setSubmittedSearch(searchValue);
    };

    const handleReset = () => {
        setPage(1);
        setSearchValue('');
        setSubmittedSearch('');
        setSearchFilter(SEARCH_FILTER_OPTIONS[0].value);
        setSubmittedSearchFilter(SEARCH_FILTER_OPTIONS[0].value);
        setLevelFilter('all');
        setMessage('');
        setErrorMessage('');
    };

    const handleLevelFilterChange = (value: string) => {
        setPage(1);
        setLevelFilter(value);
    };

    const handleRoleSelection = (userId: string, level: number) => {
        setRoleSelections((prev) => ({
            ...prev,
            [userId]: level,
        }));
    };

    const handleUpdateRole = async (user: GetUserListApiListType) => {
        const nextLevel = roleSelections[user._id] ?? user.level;
        if (nextLevel === user.level || updatingUserId) return;

        const confirmed = window.confirm(`${user.email} 계정 권한을 '${getLevelLabel(nextLevel)}'(으)로 변경할까요?`);
        if (!confirmed) return;

        setUpdatingUserId(user._id);
        setMessage('');
        setErrorMessage('');
        setLoading(true);

        try {
            const response = await userApi.userChangeLevel({ _id: user._id, level: nextLevel }, loginData.token);
            if (response.status !== 'ok') {
                throw new Error('권한 변경에 실패했습니다.');
            }

            setMessage('권한이 변경되었습니다.');
            await fetchUserList();
        } catch (error) {
            setErrorMessage(`권한 변경에 실패했습니다. ${getErrorMessage(error)}`);
        } finally {
            setUpdatingUserId('');
            setLoading(false);
        }
    };

    return (
        <DiagnosisLayout>
            <DiagnosisInnerLayout>
                <HospitalPageLabel onClick={() => window.location.reload()}>관리자 조회</HospitalPageLabel>
                <PageTitle $marginbottom='24px'>관리자 조회</PageTitle>

                <TopBar>
                    <CountText>
                        전체 <strong>{totalCount}</strong>명의 사용자
                    </CountText>
                    <SearchForm onSubmit={handleSearch}>
                        <Select value={levelFilter} onChange={(e) => handleLevelFilterChange(e.target.value)}>
                            {LEVEL_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                        <Select value={searchFilter} onChange={(e) => setSearchFilter(Number(e.target.value))}>
                            {SEARCH_FILTER_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                        <SearchInput
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            placeholder='검색어를 입력해주세요.'
                        />
                        <BasicButton $width='80px' $height='40px' $type='fill' buttonType='submit'>
                            검색
                        </BasicButton>
                        <BasicButton $width='80px' $height='40px' $type='outLine' buttonType='button' onClick={handleReset}>
                            초기화
                        </BasicButton>
                    </SearchForm>
                </TopBar>

                {(message || errorMessage) && (
                    <Notice $warning={!!errorMessage}>
                        {errorMessage || message}
                    </Notice>
                )}

                <Table>
                    <TableHeader>
                        <TableCell $width='5%'>No.</TableCell>
                        <TableCell $width='13%'>병원</TableCell>
                        <TableCell $width='18%'>이메일</TableCell>
                        <TableCell $width='8%'>이름</TableCell>
                        <TableCell $width='11%'>연락처</TableCell>
                        <TableCell $width='9%'>상태</TableCell>
                        <TableCell $width='10%'>현재 권한</TableCell>
                        <TableCell $width='18%'>권한 설정</TableCell>
                        <TableCell $width='8%'>가입일</TableCell>
                    </TableHeader>
                    {users.length === 0 ? (
                        <EmptyRow>조회된 사용자가 없습니다.</EmptyRow>
                    ) : (
                        users.map((user, idx) => {
                            const itemNumber = totalCount - (page - 1) * PAGE_LIMIT - idx;
                            const selectedRole = roleSelections[user._id] ?? user.level;
                            const isRoleChanged = selectedRole !== user.level;

                            return (
                                <TableRow key={user._id}>
                                    <TableCell $width='5%'>{itemNumber}</TableCell>
                                    <TableCell $width='13%'>{user.hospitalName || '-'}</TableCell>
                                    <TableCell $width='18%'>{user.email || '-'}</TableCell>
                                    <TableCell $width='8%'>{user.name || '-'}</TableCell>
                                    <TableCell $width='11%'>{user.mobile ? changeMobileForm(user.mobile) : '-'}</TableCell>
                                    <TableCell $width='9%'>
                                        <StatusBadge $status={user.status}>{getStatusLabel(user.status)}</StatusBadge>
                                    </TableCell>
                                    <TableCell $width='10%'>{getLevelLabel(user.level)}</TableCell>
                                    <TableCell $width='18%'>
                                        <RoleControl>
                                            <RoleSelect value={selectedRole} onChange={(e) => handleRoleSelection(user._id, Number(e.target.value))}>
                                                {CHANGEABLE_LEVEL_OPTIONS.map((option) => (
                                                    <option key={option.value} value={option.value}>
                                                        {option.label}
                                                    </option>
                                                ))}
                                            </RoleSelect>
                                            <SaveButton
                                                type='button'
                                                disabled={!isRoleChanged || updatingUserId === user._id}
                                                onClick={() => handleUpdateRole(user)}
                                            >
                                                변경
                                            </SaveButton>
                                        </RoleControl>
                                    </TableCell>
                                    <TableCell $width='8%'>
                                        {user.registrationTimestamp ? moment(user.registrationTimestamp).format('YYYY/MM/DD') : '-'}
                                    </TableCell>
                                </TableRow>
                            );
                        })
                    )}
                </Table>

                <PaginationBar>
                    <PageButton type='button' disabled={!hasPrevPage} onClick={() => setPage((prev) => Math.max(prev - 1, 1))}>
                        이전
                    </PageButton>
                    <PageNumber>
                        {page} / {totalPages || 1}
                    </PageNumber>
                    <PageButton type='button' disabled={!hasNextPage} onClick={() => setPage((prev) => prev + 1)}>
                        다음
                    </PageButton>
                </PaginationBar>
            </DiagnosisInnerLayout>
        </DiagnosisLayout>
    );
};

export default AdminUserRolePage;

const TopBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 16px;
`;

const CountText = styled(Body3Regular)`
    color: ${TextColor.color_text_light};

    strong {
        color: ${TextColor.color_text_normal};
    }
`;

const SearchForm = styled.form`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const Select = styled.select`
    width: 128px;
    height: 40px;
    padding: 0 10px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: ${Radius.radius_4};
    color: ${TextColor.color_text_normal};
    background-color: ${BackgroundColor.color_background_white};
`;

const SearchInput = styled.input`
    width: 240px;
    height: 40px;
    padding: 0 12px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: ${Radius.radius_4};
    color: ${TextColor.color_text_normal};
`;

const Notice = styled(Body5Regular)<{ $warning: boolean }>`
    width: 100%;
    padding: 12px 14px;
    margin-bottom: 12px;
    color: ${({ $warning }) => ($warning ? WarningColor.color_warning_normal : TextColor.color_text_light)};
    border: 1px solid ${BorderColor.color_border_neutral};
    border-radius: ${Radius.radius_4};
    background-color: ${({ $warning }) => ($warning ? BackgroundColor.color_background_warning : BackgroundColor.color_background_neutral)};
`;

const Table = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    min-height: 520px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: ${Radius.radius_4};
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: flex;
    width: 100%;
    min-height: 52px;
    border-bottom: 1px solid ${BorderColor.color_border_neutral};
    background-color: ${BackgroundColor.color_background_neutral};
`;

const TableRow = styled.div`
    display: flex;
    width: 100%;
    min-height: 56px;
    border-bottom: 1px solid ${BorderColor.color_border_weak};

    &:last-child {
        border-bottom: 0;
    }
`;

const TableCell = styled(Body3Medium)<{ $width: string }>`
    display: flex;
    align-items: center;
    width: ${({ $width }) => $width};
    min-width: 0;
    padding: 10px 12px;
    color: ${TextColor.color_text_normal};
    word-break: break-all;
`;

const EmptyRow = styled(Body2Bold)`
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    color: ${TextColor.color_text_light};
`;

const StatusBadge = styled.span<{ $status: number }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 72px;
    height: 28px;
    padding: 0 10px;
    border-radius: ${Radius.radius_circle};
    color: ${({ $status }) => ($status === AccountStatus.reject ? WarningColor.color_warning_normal : TextColor.color_text_neutral)};
    background-color: ${({ $status }) => ($status === AccountStatus.active ? BackgroundColor.color_background_primary_normal : $status === AccountStatus.reject ? BackgroundColor.color_background_warning : BackgroundColor.color_background_neutral)};
`;

const RoleControl = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    width: 100%;
`;

const RoleSelect = styled.select`
    width: 112px;
    height: 36px;
    padding: 0 8px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: ${Radius.radius_4};
    color: ${TextColor.color_text_normal};
    background-color: ${BackgroundColor.color_background_white};
`;

const SaveButton = styled.button`
    width: 48px;
    height: 36px;
    border-radius: ${Radius.radius_4};
    color: ${TextColor.color_text_light};
    border: 1px solid ${BorderColor.color_border_strong};
    background-color: ${BackgroundColor.color_background_white};

    &:disabled {
        color: ${TextColor.color_text_disabled};
        border-color: ${BorderColor.color_border_normal};
        background-color: ${BackgroundColor.color_background_disabled};
        cursor: not-allowed;
    }
`;

const PaginationBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
`;

const PageButton = styled.button`
    width: 64px;
    height: 36px;
    border: 1px solid ${BorderColor.color_border_normal};
    border-radius: ${Radius.radius_4};
    color: ${TextColor.color_text_normal};
    background-color: ${BackgroundColor.color_background_white};

    &:disabled {
        color: ${TextColor.color_text_disabled};
        background-color: ${BackgroundColor.color_background_disabled};
        cursor: not-allowed;
    }
`;

const PageNumber = styled(Body3Medium)`
    min-width: 72px;
    text-align: center;
    color: ${TextColor.color_text_light};
`;
